import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../api/axios';
import './OrgChartModal.css';

const normalizeRoleType = (member) => {
  const role = String(member.workspace_role || member.role || member.displayRole || '')
    .toLowerCase()
    .trim();
  const isOwner =
    member.is_owner === true ||
    role === 'org_owner' ||
    role === 'owner' ||
    role === 'organization_owner';

  if (isOwner) return 'owner';
  if (role === 'org_admin' || role === 'admin' || role === 'organization_admin') return 'admin';
  if (role === 'org_sales_manager' || role === 'sales_manager' || role === 'manager')
    return 'manager';
  if (role === 'org_researcher' || role === 'researcher') return 'researcher';
  if (role === 'org_viewer' || role === 'viewer') return 'viewer';
  return 'member'; // default to member / sales_member
};

const getRoleBadgeConfig = (roleType) => {
  switch (roleType) {
    case 'owner':
      return {
        label: 'Owner',
        icon: 'fas fa-crown',
        bg: '#fffbeb',
        color: '#b45309',
        border: '#fde68a',
        level: 1,
      };
    case 'admin':
      return {
        label: 'Admin',
        icon: 'fas fa-shield-alt',
        bg: '#eff6ff',
        color: '#1d4ed8',
        border: '#bfdbfe',
        level: 2,
      };
    case 'manager':
      return {
        label: 'Manager',
        icon: 'fas fa-user-tie',
        bg: '#f5f3ff',
        color: '#6d28d9',
        border: '#ddd6fe',
        level: 3,
      };
    case 'researcher':
      return {
        label: 'Researcher',
        icon: 'fas fa-binoculars',
        bg: '#ecfeff',
        color: '#0e7490',
        border: '#a5f3fc',
        level: 4,
      };
    case 'viewer':
      return {
        label: 'Viewer',
        icon: 'fas fa-eye',
        bg: '#f1f5f9',
        color: '#475569',
        border: '#e2e8f0',
        level: 4,
      };
    default:
      return {
        label: 'Member',
        icon: 'fas fa-user',
        bg: '#ecfdf5',
        color: '#047857',
        border: '#a7f3d0',
        level: 4,
      };
  }
};

const OrgMemberCard = ({ member, roleType, showRoleBadge = false }) => {
  const badgeConfig = getRoleBadgeConfig(roleType);
  const displayName =
    member.display_name ||
    (member.first_name ? `${member.first_name} ${member.last_name || ''}`.trim() : null) ||
    member.username ||
    (member.email ? member.email.split('@')[0] : 'Member');

  const initials =
    displayName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join('') || 'U';

  const isPending = member.membership_status === 'pending_invite' || member.is_active === false;

  let avatarClass = 'av-member';
  if (roleType === 'owner') avatarClass = 'av-owner';
  else if (roleType === 'admin') avatarClass = 'av-admin';
  else if (roleType === 'manager') avatarClass = 'av-manager';
  else if (roleType === 'researcher') avatarClass = 'av-researcher';
  else if (roleType === 'viewer') avatarClass = 'av-viewer';

  return (
    <div className="org-card-modern" title={`${displayName} (${badgeConfig.label})`}>
      <div className={`org-card-avatar-circle ${avatarClass}`}>{initials}</div>
      <div className="org-card-details">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px',
          }}
        >
          <span className="org-card-title">{displayName}</span>
          {showRoleBadge && (
            <span
              className="org-card-role-chip"
              style={{
                backgroundColor: badgeConfig.bg,
                color: badgeConfig.color,
                border: `1px solid ${badgeConfig.border}`,
              }}
            >
              <i className={badgeConfig.icon} style={{ fontSize: '8px', marginRight: '3px' }}></i>
              {badgeConfig.label}
            </span>
          )}
          {isPending && <span className="org-card-pending-badge">Pending</span>}
        </div>
        <span className="org-card-email-text">{member.email || '—'}</span>
      </div>
    </div>
  );
};

const OrgChartModal = ({ organization, onClose }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('tree'); // 'tree' | 'directory'
  const [activeTierFilter, setActiveTierFilter] = useState('all'); // 'all' | 'lv1' | 'lv2' | 'lv3' | 'lv4'

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!organization?.id) return;

    let isMounted = true;
    const fetchMembers = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/workspace/${organization.id}/members`);
        if (isMounted) {
          if (res.data.success) {
            setMembers(res.data.members || []);
          } else {
            setError(res.data.message || 'Failed to fetch members');
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching org members for org chart:', err);
          setError(err.response?.data?.message || 'Failed to load organization hierarchy');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMembers();

    return () => {
      isMounted = false;
    };
  }, [organization?.id]);

  // Group members into 4 levels
  const { level1, level2, level3, level4, allCount } = useMemo(() => {
    let effectiveMembers = [...members];

    // Fallback: If no owner found in fetched members list, but organization has owner_email, create fallback owner
    const hasOwner = effectiveMembers.some((m) => normalizeRoleType(m) === 'owner');
    if (!hasOwner && organization?.owner_email) {
      effectiveMembers.unshift({
        user_id: 'owner-fallback',
        display_name: organization.owner_email.split('@')[0],
        email: organization.owner_email,
        workspace_role: 'org_owner',
        is_owner: true,
        is_active: true,
      });
    }

    // Apply search filter if query is present
    const filtered = effectiveMembers.filter((m) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const name = (m.display_name || m.first_name || m.username || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      const role = (m.workspace_role || m.role || '').toLowerCase();
      return name.includes(term) || email.includes(term) || role.includes(term);
    });

    const l1 = [];
    const l2 = [];
    const l3 = [];
    const l4 = [];

    filtered.forEach((member) => {
      const type = normalizeRoleType(member);
      if (type === 'owner') {
        l1.push({ member, type });
      } else if (type === 'admin') {
        l2.push({ member, type });
      } else if (type === 'manager') {
        l3.push({ member, type });
      } else {
        l4.push({ member, type });
      }
    });

    return {
      level1: l1,
      level2: l2,
      level3: l3,
      level4: l4,
      allCount: effectiveMembers.length,
    };
  }, [members, organization, searchTerm]);

  return (
    <div
      className="org-chart-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="org-chart-title"
    >
      <div className="org-chart-modal">
        {/* Header */}
        <div className="org-chart-header">
          <div className="org-chart-title-area">
            <div className="org-chart-org-avatar">
              {organization?.name?.substring(0, 2).toUpperCase() || 'OR'}
            </div>
            <div className="org-chart-title-group">
              <h2 id="org-chart-title">
                {organization?.name}
                <span className="org-chart-plan-badge">{organization?.plan || 'Free'}</span>
              </h2>
              <p className="org-chart-subtitle">
                <i className="fas fa-sitemap" style={{ color: '#3b82f6' }}></i> Organization
                Hierarchy Chart
                {organization?.industry && <span>· {organization.industry}</span>}
              </p>
            </div>
          </div>

          <div className="org-chart-header-actions">
            <button className="org-chart-close-btn" onClick={onClose} aria-label="Close modal">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Toolbar with View Toggle, Level Filters & Search */}
        <div className="org-chart-toolbar">
          <div className="org-chart-view-toggle">
            <button
              type="button"
              className={`org-view-btn ${viewMode === 'tree' ? 'active' : ''}`}
              onClick={() => setViewMode('tree')}
            >
              <i className="fas fa-sitemap"></i> Tree View
            </button>
            <button
              type="button"
              className={`org-view-btn ${viewMode === 'directory' ? 'active' : ''}`}
              onClick={() => setViewMode('directory')}
            >
              <i className="fas fa-list"></i> Directory
            </button>
          </div>

          <div className="org-chart-filters">
            <button
              type="button"
              className={`org-tier-filter-pill ${activeTierFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTierFilter('all')}
            >
              All <span className="count">{allCount}</span>
            </button>
            <button
              type="button"
              className={`org-tier-filter-pill ${activeTierFilter === 'lv1' ? 'active' : ''}`}
              onClick={() => setActiveTierFilter('lv1')}
            >
              Lv 1 Owner <span className="count">{level1.length}</span>
            </button>
            <button
              type="button"
              className={`org-tier-filter-pill ${activeTierFilter === 'lv2' ? 'active' : ''}`}
              onClick={() => setActiveTierFilter('lv2')}
            >
              Lv 2 Admin <span className="count">{level2.length}</span>
            </button>
            <button
              type="button"
              className={`org-tier-filter-pill ${activeTierFilter === 'lv3' ? 'active' : ''}`}
              onClick={() => setActiveTierFilter('lv3')}
            >
              Lv 3 Manager <span className="count">{level3.length}</span>
            </button>
            <button
              type="button"
              className={`org-tier-filter-pill ${activeTierFilter === 'lv4' ? 'active' : ''}`}
              onClick={() => setActiveTierFilter('lv4')}
            >
              Lv 4 Team <span className="count">{level4.length}</span>
            </button>
          </div>

          <div className="org-chart-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              className="org-chart-search-input"
              placeholder="Search member..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Modal Body */}
        <div className="org-chart-body">
          {loading ? (
            <div className="org-chart-loading">
              <i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: '#3b82f6' }}></i>
              <span>Loading organizational structure...</span>
            </div>
          ) : error ? (
            <div className="org-chart-error">
              <i className="fas fa-exclamation-triangle fa-2x"></i>
              <span>{error}</span>
            </div>
          ) : viewMode === 'tree' ? (
            /* Tree Canvas View */
            <div className="org-tree-flow">
              {/* LEVEL 1 — OWNER */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv1') && (
                <div className="org-tree-tier">
                  <div className="org-tier-tag lv1">
                    <i className="fas fa-crown"></i> Level 1 · Owner
                  </div>
                  <div className="org-tier-nodes">
                    {level1.length > 0 ? (
                      level1.map(({ member, type }) => (
                        <OrgMemberCard
                          key={member.user_id || member.email}
                          member={member}
                          roleType={type}
                        />
                      ))
                    ) : (
                      <span className="org-tier-empty">No owner profile</span>
                    )}
                  </div>
                </div>
              )}

              {/* Connecting line */}
              {activeTierFilter === 'all' && <div className="org-tree-line-v"></div>}

              {/* LEVEL 2 — ADMIN */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv2') && (
                <div className="org-tree-tier">
                  <div className="org-tier-tag lv2">
                    <i className="fas fa-shield-alt"></i> Level 2 · Admin
                  </div>
                  <div className="org-tier-nodes">
                    {level2.length > 0 ? (
                      level2.map(({ member, type }) => (
                        <OrgMemberCard
                          key={member.user_id || member.membership_id || member.email}
                          member={member}
                          roleType={type}
                        />
                      ))
                    ) : (
                      <span className="org-tier-empty">No administrators assigned</span>
                    )}
                  </div>
                </div>
              )}

              {/* Connecting line */}
              {activeTierFilter === 'all' && <div className="org-tree-line-v"></div>}

              {/* LEVEL 3 — MANAGER */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv3') && (
                <div className="org-tree-tier">
                  <div className="org-tier-tag lv3">
                    <i className="fas fa-user-tie"></i> Level 3 · Manager
                  </div>
                  <div className="org-tier-nodes">
                    {level3.length > 0 ? (
                      level3.map(({ member, type }) => (
                        <OrgMemberCard
                          key={member.user_id || member.membership_id || member.email}
                          member={member}
                          roleType={type}
                        />
                      ))
                    ) : (
                      <span className="org-tier-empty">No managers appointed</span>
                    )}
                  </div>
                </div>
              )}

              {/* Connecting line */}
              {activeTierFilter === 'all' && <div className="org-tree-line-v"></div>}

              {/* LEVEL 4 — MEMBER & VIEWER & RESEARCHER */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv4') && (
                <div className="org-tree-tier">
                  <div className="org-tier-tag lv4">
                    <i className="fas fa-users"></i> Level 4 · Member, Researcher & Viewer
                  </div>
                  <div className="org-tier-nodes">
                    {level4.length > 0 ? (
                      level4.map(({ member, type }) => (
                        <OrgMemberCard
                          key={member.user_id || member.membership_id || member.email}
                          member={member}
                          roleType={type}
                          showRoleBadge={true}
                        />
                      ))
                    ) : (
                      <span className="org-tier-empty">No Level 4 team members</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Directory Grouped List View */
            <div className="org-directory-view">
              {/* Level 1 Group */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv1') && (
                <div className="org-directory-group">
                  <div className="org-directory-group-header">
                    <span>
                      <i
                        className="fas fa-crown"
                        style={{ color: '#b45309', marginRight: '6px' }}
                      ></i>{' '}
                      Level 1 · Owner
                    </span>
                    <span>{level1.length} user(s)</span>
                  </div>
                  <div className="org-directory-list">
                    {level1.map(({ member }) => (
                      <div key={member.user_id || member.email} className="org-directory-item">
                        <div className="org-directory-left">
                          <div className="org-card-avatar-circle av-owner">
                            {member.display_name?.charAt(0)?.toUpperCase() || 'O'}
                          </div>
                          <div>
                            <span className="org-directory-name">
                              {member.display_name || member.email}
                            </span>
                            <span className="org-directory-email">{member.email}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Level 2 Group */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv2') && (
                <div className="org-directory-group">
                  <div className="org-directory-group-header">
                    <span>
                      <i
                        className="fas fa-shield-alt"
                        style={{ color: '#1d4ed8', marginRight: '6px' }}
                      ></i>{' '}
                      Level 2 · Admin
                    </span>
                    <span>{level2.length} user(s)</span>
                  </div>
                  <div className="org-directory-list">
                    {level2.length > 0 ? (
                      level2.map(({ member }) => (
                        <div
                          key={member.user_id || member.membership_id || member.email}
                          className="org-directory-item"
                        >
                          <div className="org-directory-left">
                            <div className="org-card-avatar-circle av-admin">
                              {member.display_name?.charAt(0)?.toUpperCase() || 'A'}
                            </div>
                            <div>
                              <span className="org-directory-name">
                                {member.display_name || member.email}
                              </span>
                              <span className="org-directory-email">{member.email}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: '12px 16px',
                          color: '#94a3b8',
                          fontSize: '12px',
                          fontStyle: 'italic',
                        }}
                      >
                        No administrators
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Level 3 Group */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv3') && (
                <div className="org-directory-group">
                  <div className="org-directory-group-header">
                    <span>
                      <i
                        className="fas fa-user-tie"
                        style={{ color: '#6d28d9', marginRight: '6px' }}
                      ></i>{' '}
                      Level 3 · Manager
                    </span>
                    <span>{level3.length} user(s)</span>
                  </div>
                  <div className="org-directory-list">
                    {level3.length > 0 ? (
                      level3.map(({ member }) => (
                        <div
                          key={member.user_id || member.membership_id || member.email}
                          className="org-directory-item"
                        >
                          <div className="org-directory-left">
                            <div className="org-card-avatar-circle av-manager">
                              {member.display_name?.charAt(0)?.toUpperCase() || 'M'}
                            </div>
                            <div>
                              <span className="org-directory-name">
                                {member.display_name || member.email}
                              </span>
                              <span className="org-directory-email">{member.email}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: '12px 16px',
                          color: '#94a3b8',
                          fontSize: '12px',
                          fontStyle: 'italic',
                        }}
                      >
                        No managers
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Level 4 Group */}
              {(activeTierFilter === 'all' || activeTierFilter === 'lv4') && (
                <div className="org-directory-group">
                  <div className="org-directory-group-header">
                    <span>
                      <i
                        className="fas fa-users"
                        style={{ color: '#047857', marginRight: '6px' }}
                      ></i>{' '}
                      Level 4 · Member, Researcher & Viewer
                    </span>
                    <span>{level4.length} user(s)</span>
                  </div>
                  <div className="org-directory-list">
                    {level4.length > 0 ? (
                      level4.map(({ member, type }) => {
                        const cfg = getRoleBadgeConfig(type);
                        return (
                          <div
                            key={member.user_id || member.membership_id || member.email}
                            className="org-directory-item"
                          >
                            <div className="org-directory-left">
                              <div className="org-card-avatar-circle av-member">
                                {member.display_name?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <span className="org-directory-name">
                                  {member.display_name || member.email}
                                </span>
                                <span className="org-directory-email">{member.email}</span>
                              </div>
                            </div>
                            <span
                              className="org-card-role-chip"
                              style={{
                                backgroundColor: cfg.bg,
                                color: cfg.color,
                                border: `1px solid ${cfg.border}`,
                              }}
                            >
                              <i className={cfg.icon} style={{ marginRight: '4px' }}></i>
                              {cfg.label}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div
                        style={{
                          padding: '12px 16px',
                          color: '#94a3b8',
                          fontSize: '12px',
                          fontStyle: 'italic',
                        }}
                      >
                        No team members
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="org-chart-footer">
          <div className="org-chart-footer-info">
            <i className="fas fa-layer-group"></i>
            <span>
              Hierarchy: Lv 1 (Owner) → Lv 2 (Admin) → Lv 3 (Manager) → Lv 4 (Member / Researcher /
              Viewer)
            </span>
          </div>
          <button type="button" className="org-chart-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgChartModal;
